import { Component, computed, effect, inject, signal, untracked, ViewChild } from '@angular/core';
import { AppConfigService, Log } from '@core/services/config/app-config.service';
import { logStorage, emptyStorage } from 'src/app/app.config';
import { Router, RouterModule } from '@angular/router';
import { faUserXmark, faBagShopping, faCartShopping, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { faUser, faHeart } from '@fortawesome/free-regular-svg-icons';
import { faUserCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeModule, FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { AnimationProp, FaProps } from '@fortawesome/angular-fontawesome';
import { TranslatePipe } from 'src/app/core/pipe/translate.pipe';
import { UserStoreService } from '@core/services/storage/user/user-store.service';
import { LangComponent } from '@components/shared/lang/lang.component';
import { BackdropService } from '@components/shared/backdrop/backdrop.service';
import { CartSidebarService } from '@components/shared/cart/cart-sidebar/cart-sidebar.service';
import { CartCookieService } from '@core/services/storage/cart/cart-cookie.service';
import { debounceTime, distinctUntilChanged, map, skip } from 'rxjs';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FadeInUpDirective } from '@components/shared/fade-in-up.directive';
import { CurrencyPipe } from '@angular/common';
import { WishlistService } from '@components/wishlist/wishlist.service';
import { SeasonalThemeService } from '@core/services/theme/seasonal-theme.service';
import { HalloweenHeaderDecorComponent } from '@components/shared/halloween/halloween-header-decor.component';

// Definiamo un'interfaccia per l'oggetto animazione
interface AnimationObject {
    animation: string;
}
const animation_effect = {
    fade: 'fade',
    bounce: 'bounce',
    beat: 'beat',
    beatFade: 'beat-fade',
    spin: 'spin',
    flip: 'flip',
    shake: 'shake'
}
@Component({
    selector: 'app-header',
    imports: [
        RouterModule,
        FontAwesomeModule,
        TranslatePipe,
        LangComponent,
        FadeInUpDirective,
        CurrencyPipe,
        HalloweenHeaderDecorComponent
    ],
    templateUrl: './header.component.html',
    styleUrl: './header.component.scss'
})
export class HeaderComponent {
    authService = inject(UserStoreService);
    faUserUnlogged = faUserXmark;
    faUserLogged = faUserCheck;
    faWishList = faHeart;
    faCartShopping = faCartShopping; //faBagShopping;
    faSearch = faMagnifyingGlass;

    nome: string = '';

    private iconLibrary = inject(FaIconLibrary);

    //services
    private backdropService = inject(BackdropService);
    private cartSidebarService = inject(CartSidebarService)
    cartCookieService = inject(CartCookieService);
    private appConfig = inject(AppConfigService);
    public wishlist = inject(WishlistService);
    protected readonly seasonalTheme = inject(SeasonalThemeService);

    cartIconAnimation: AnimationObject = { animation: '' };

    private router = inject(Router);

    //Stringa di ricerca per input nella box-> vedi effect nel constructor
    public search_query = signal('');
    private debounceTimeMS:number = 500

    readonly logo = computed(() => 
        this.authService.isProSignal() && this.authService.isLoggedAndNoGuestSignal()
            ? 'images/loghi/logo_pro.png'
            : 'images/loghi/logo.webp'
    );

    // signal per calcolare il totale carrello
    readonly cartTotal = computed(() => {
        this.cartCookieService.cartChanged(); // tracker
        // this.cartCookieService.cartChanged(); // forza il tracciamento

        const articoli = this.cartCookieService.cartValue().articoli;
        const paga = this.authService.paga();
        const isPro = this.authService.isProSignal();
        const isLogged = this.authService.isLoggedSignal();
        const isGuest = this.authService.isGuestSignal();

        return articoli.reduce((sum, item) => {
            let prezzo: number = item.prezzo_privati_vat; // default — non loggati

                // non loggati
                // default

                // LOGGATI PRO
                if(this.authService.isProSignal()) {
                    prezzo = item.prezzo_pro_novat;
                }


                // LOGGATI NO PRO EXTRA UE
                if(!this.authService.isProSignal() && this.authService.isEXTRAUE()) {
                    prezzo = item.prezzo_privati_novat;
                }

                // LOGGATI NO PRO UE -->
                if(!this.authService.isProSignal() && !this.authService.isEXTRAUE()) {
                    prezzo = item.prezzo_privati_vat;
                }

                if(this.appConfig.isComORcoUk()) {
                    prezzo = item.prezzo_privati_novat;

                    if(this.authService.isProSignal()) {
                        prezzo = item.prezzo_pro_novat;
                    }
                }

            return sum + prezzo * item.item_quantita;
        }, 0);
    });
    
    constructor() {
        effect(() => {
            if (this.cartCookieService.cartChanged()) {
                // animazione icona carrello
                this.setIconAnimation(this.cartIconAnimation, animation_effect.shake);
            }

        });

        toObservable(this.search_query).pipe(
            skip(1),
            debounceTime(this.debounceTimeMS),
            map(q => this.normalizeQuery(q)), // <-- normalizza PRIMA
            distinctUntilChanged(), // ora dedup-a sui valori puliti
            takeUntilDestroyed()
        ).subscribe(query => this.searchByQuery(query));
    }

    private normalizeQuery(q: string): string {
        return (q ?? '').trim().replace(/\s+/g, ' ');
    }

    private searchByQuery(rawQuery: string) {
        // Controlla se la stringa non è vuota e non è solo spazi
        const searchRoute: string = this.appConfig.i18n['ROUTE.ricerca'];

        // normalizzazione trailing space e inner space
        const query = this.normalizeQuery(rawQuery);

        if (query.length > 2) {
            this.router.navigate([searchRoute, encodeURIComponent(query)]);

        }
    }

    onSearchSubmit() {
        const query = this.search_query();
        if(query) {
            this.searchByQuery(query);
        }
    }

    ngOnInit() {

        const searchRoute = this.appConfig.i18n['ROUTE.ricerca'];
        const match = this.router.url.match(`/${searchRoute}/(.+)`);
        if (match?.[1]) {
            this.search_query.set(decodeURIComponent(match[1]));
        }

        this.iconLibrary.addIcons(faUserXmark, faUser);
    }

    toggleMenu() {
        this.backdropService.show();
        const nav = document.querySelector('.emp-nav');
        /* const overlay = document.querySelector('.overlay'); */

        if (nav?.classList.contains('active')) {
            // menu visualizzato, lo nascondo e nasconto l'overlay
            nav?.classList.remove('active');
            /* overlay?.classList.remove('active'); */
        } else {
            // menu non visualizzato, lo mostro e mostro l'overlay
            nav?.classList.add('active');
            /* overlay?.classList.add('active'); */
        }

    }


    viewCache() {
        logStorage();
    }
    clearCache() {
        emptyStorage();
    }

    openCart() {
        this.backdropService.show();
        this.cartSidebarService.open();
    }

    setIconAnimation(obj: AnimationObject, animation: string) {
        obj.animation = animation;
        setTimeout(() => {
            obj.animation = '';
        }, 2000);
    }

}
